
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, User, Plus, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { debugLogger } from '@/utils/debugLogger';

interface AddUserToMissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (userId: string) => void;
  isAdding: boolean;
  missionId: string;
}

const AddUserToMissionModal: React.FC<AddUserToMissionModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
  isAdding,
  missionId
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: availableUsers, isLoading } = useQuery({
    queryKey: ['available-users-for-mission', missionId, searchTerm],
    queryFn: async () => {
      // Get users who are not already in this mission
      const { data: existingParticipants, error: participantsError } = await supabase
        .from('user_missions')
        .select('user_id')
        .eq('mission_id', missionId);

      if (participantsError) {
        debugLogger.error('Error fetching existing participants:', participantsError);
        throw participantsError;
      }

      const excludeUserIds = existingParticipants?.map(p => p.user_id) || [];

      // Build query for available users
      let query = supabase
        .from('profiles')
        .select('id, username, main_photo_url, verification_level')
        .eq('onboarding_completed', true)
        .eq('hidden', false);

      // Exclude users already in mission
      if (excludeUserIds.length > 0) {
        query = query.not('id', 'in', `(${excludeUserIds.join(',')})`);
      }

      // Filter by search term
      if (searchTerm) {
        query = query.ilike('username', `%${searchTerm}%`);
      }

      const { data, error } = await query
        .order('username')
        .limit(20);

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen
  });

  const handleAddUser = (userId: string) => {
    onAddUser(userId);
    setSearchTerm('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-black/95 border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-luxury-red" />
            Adicionar Usuário à Missão
          </DialogTitle>
          <DialogDescription className="sr-only">
            Busque e adicione usuários disponíveis a esta missão
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="search" className="text-white/80">
              Buscar usuário por username
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
              <Input
                id="search"
                type="text"
                placeholder="Digite o username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
              />
            </div>
          </div>

          {/* Users List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-white/60" />
              </div>
            ) : availableUsers?.length === 0 ? (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-white/40 mx-auto mb-2" />
                <p className="text-white/60 text-sm">
                  {searchTerm ? 'Nenhum usuário encontrado' : 'Todos os usuários já estão na missão'}
                </p>
              </div>
            ) : (
              availableUsers?.map((user) => (
                <Card key={user.id} className="bg-white/5 border-white/10">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.main_photo_url || ''} alt={user.username} />
                          <AvatarFallback className="bg-luxury-purple/20 text-white">
                            {user.username?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-medium">@{user.username}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge 
                              variant="secondary" 
                              className={`text-xs ${
                                user.verification_level === 'full' 
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}
                            >
                              {user.verification_level === 'full' ? 'Verificado' : 'Básico'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="admin-primary"
                        onClick={() => handleAddUser(user.id)}
                        disabled={isAdding}
                        className="flex-shrink-0"
                      >
                        {isAdding ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
            <Button variant="admin-outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserToMissionModal;
